// ファイル名: line-friend-banner.js
// LINE公式アカウント友だち募集バナー - インタラクション制御
// ES6準拠、jQuery非依存、アクセシビリティ強化対応

/**
 * LINE友だち募集バナーセクションの初期化と制御
 * - IntersectionObserverベースのスクロールアニメーション
 * - キーボードアクセシビリティ対応
 * - パフォーマンス最適化
 * - エラーハンドリング
 */

class LineFriendBanner {
  constructor() {
    // セクション要素の取得
    this.sections = document.querySelectorAll('[data-section-type="line-friend-banner"]');
    
    // 初期化フラグ
    this.initialized = false;
    
    // IntersectionObserver設定
    this.observerOptions = {
      root: null,
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.1
    };
    
    // バインド処理
    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    
    // 初期化実行
    this.init();
  }
  
  /**
   * 初期化処理
   * - DOM準備完了確認
   * - 各セクションの設定
   * - IntersectionObserver設定
   */
  init() {
    try {
      // DOM準備確認
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setupSections());
      } else {
        this.setupSections();
      }
    } catch (error) {
      console.error('LINE友だちバナー初期化エラー:', error);
    }
  }
  
  /**
   * セクション設定処理
   * - 各セクションに対するイベントリスナー設定
   * - IntersectionObserver設定
   */
  setupSections() {
    if (this.sections.length === 0) return;
    
    try {
      // IntersectionObserver設定（サポート確認付き）
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          this.handleIntersection,
          this.observerOptions
        );
        
        // 各セクションの観察開始
        this.sections.forEach(section => {
          this.observer.observe(section);
          this.setupSectionEvents(section);
        });
      } else {
        // IntersectionObserver非対応の場合はフォールバック
        this.sections.forEach(section => {
          section.classList.add('is-visible');
          this.setupSectionEvents(section);
        });
      }
      
      this.initialized = true;
      
    } catch (error) {
      console.error('セクション設定エラー:', error);
    }
  }
  
  /**
   * 個別セクションのイベント設定
   * @param {Element} section - 対象セクション要素
   */
  setupSectionEvents(section) {
    try {
      // ボタン要素の取得
      const button = section.querySelector('[data-line-friend-button]');
      
      if (!button) return;
      
      // クリックイベント設定
      button.addEventListener('click', this.handleButtonClick);
      
      // キーボードイベント設定
      button.addEventListener('keydown', this.handleKeyPress);
      
      // フォーカス管理
      this.setupFocusManagement(button);
      
    } catch (error) {
      console.error('セクションイベント設定エラー:', error);
    }
  }
  
  /**
   * IntersectionObserverコールバック
   * @param {Array} entries - 観察対象要素の配列
   */
  handleIntersection(entries) {
    entries.forEach(entry => {
      try {
        if (entry.isIntersecting) {
          // 表示時の処理
          entry.target.classList.add('is-visible');
          
          // アニメーション実行確認のためのログ（開発時のみ）
          if (process.env.NODE_ENV === 'development') {
            console.log('LINE友だちバナーが表示されました:', entry.target.id);
          }
          
          // 一度表示されたら観察を停止（パフォーマンス最適化）
          this.observer?.unobserve(entry.target);
        }
      } catch (error) {
        console.error('Intersectionハンドリングエラー:', error);
      }
    });
  }
  
  /**
   * ボタンクリック処理
   * @param {Event} event - クリックイベント
   */
  handleButtonClick(event) {
    try {
      const button = event.currentTarget;
      const href = button.getAttribute('href');
      
      // 無効なリンクの場合は処理を中止
      if (!href || href === '#') {
        event.preventDefault();
        console.warn('LINE友だち追加URLが設定されていません');
        return;
      }
      
      // LINE公式URLの検証
      if (href.includes('line.me')) {
        // LINE友だち追加のアナリティクス送信（Google Analytics対応）
        this.trackLineFriendAddClick(button);
        
        // LINE公式URLの場合は新しいタブで開く
        if (!button.hasAttribute('target')) {
          event.preventDefault();
          window.open(href, '_blank', 'noopener,noreferrer');
        }
      }
      
      // フィードバック表示
      this.showClickFeedback(button);
      
    } catch (error) {
      console.error('ボタンクリック処理エラー:', error);
    }
  }
  
  /**
   * キーボード操作処理
   * @param {Event} event - キーボードイベント
   */
  handleKeyPress(event) {
    try {
      // Enter または Spaceキーでボタンクリックをトリガー
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.currentTarget.click();
      }
      
      // Escapeキーでフォーカス解除
      if (event.key === 'Escape') {
        event.currentTarget.blur();
      }
      
    } catch (error) {
      console.error('キーボード操作エラー:', error);
    }
  }
  
  /**
   * フォーカス管理設定
   * @param {Element} button - ボタン要素
   */
  setupFocusManagement(button) {
    try {
      // フォーカス取得時の処理
      button.addEventListener('focus', () => {
        button.setAttribute('data-focus-visible', 'true');
      });
      
      // フォーカス喪失時の処理
      button.addEventListener('blur', () => {
        button.removeAttribute('data-focus-visible');
      });
      
      // マウス操作時はフォーカス表示を抑制
      button.addEventListener('mousedown', () => {
        button.setAttribute('data-mouse-focus', 'true');
      });
      
      button.addEventListener('mouseup', () => {
        // 少し遅延してからマウスフォーカスフラグを削除
        setTimeout(() => {
          button.removeAttribute('data-mouse-focus');
        }, 100);
      });
      
    } catch (error) {
      console.error('フォーカス管理設定エラー:', error);
    }
  }
  
  /**
   * クリックフィードバック表示
   * @param {Element} button - クリックされたボタン
   */
  showClickFeedback(button) {
    try {
      // リップル効果の追加
      button.style.transform = 'scale(0.98)';
      
      // アニメーション後に元に戻す
      setTimeout(() => {
        button.style.transform = '';
      }, 150);
      
    } catch (error) {
      console.error('クリックフィードバックエラー:', error);
    }
  }
  
  /**
   * LINE友だち追加クリックのトラッキング
   * @param {Element} button - クリックされたボタン
   */
  trackLineFriendAddClick(button) {
    try {
      // Google Analytics 4 (gtag) がある場合
      if (typeof gtag === 'function') {
        gtag('event', 'line_friend_add_click', {
          event_category: 'engagement',
          event_label: 'LINE公式アカウント友だち追加',
          value: 1
        });
      }
      
      // Universal Analytics (ga) がある場合
      if (typeof ga === 'function') {
        ga('send', 'event', 'LINE', 'friend_add_click', 'バナーからの友だち追加');
      }
      
      // カスタムイベントの発火（他のスクリプトで利用可能）
      const customEvent = new CustomEvent('lineFriendAddClick', {
        detail: {
          buttonElement: button,
          timestamp: new Date().toISOString(),
          url: button.getAttribute('href')
        }
      });
      
      document.dispatchEvent(customEvent);
      
    } catch (error) {
      console.error('トラッキングエラー:', error);
    }
  }
  
  /**
   * セクションの破棄処理
   * - メモリリーク防止
   * - イベントリスナーの削除
   */
  destroy() {
    try {
      // IntersectionObserverの停止
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      
      // イベントリスナーの削除
      this.sections.forEach(section => {
        const button = section.querySelector('[data-line-friend-button]');
        if (button) {
          button.removeEventListener('click', this.handleButtonClick);
          button.removeEventListener('keydown', this.handleKeyPress);
        }
      });
      
      this.initialized = false;
      
    } catch (error) {
      console.error('セクション破棄エラー:', error);
    }
  }
}

/**
 * セクション初期化処理
 * - DOMContentLoaded後に自動実行
 * - Shopifyのセクション再読み込み対応
 */

// グローバル変数として実例を保持
let lineFriendBannerInstance = null;

// 初期化処理
function initLineFriendBanner() {
  try {
    // 既存のインスタンスがある場合は破棄
    if (lineFriendBannerInstance) {
      lineFriendBannerInstance.destroy();
    }
    
    // 新しいインスタンスを作成
    lineFriendBannerInstance = new LineFriendBanner();
    
  } catch (error) {
    console.error('LINE友だちバナー初期化エラー:', error);
  }
}

// DOM準備完了時の初期化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLineFriendBanner);
} else {
  initLineFriendBanner();
}

// Shopifyセクション再読み込み対応
document.addEventListener('shopify:section:load', (event) => {
  if (event.detail.sectionId && 
      event.target.querySelector('[data-section-type="line-friend-banner"]')) {
    initLineFriendBanner();
  }
});

// Shopifyセクション削除時の処理
document.addEventListener('shopify:section:unload', (event) => {
  if (event.detail.sectionId && 
      event.target.querySelector('[data-section-type="line-friend-banner"]')) {
    if (lineFriendBannerInstance) {
      lineFriendBannerInstance.destroy();
      lineFriendBannerInstance = null;
    }
  }
});

// モジュールとしてエクスポート（必要に応じて）
if (typeof module !== 'undefined' && module.exports) {
  module.exports = LineFriendBanner;
}